/**
 * Audit — Phase 2 audit console (the compliance money-shot).
 *
 * Flow:
 *   load    -> GET /api/audit/events  -> render the hash-chained access decisions
 *   verify  -> GET /api/audit/verify  -> show the chain-integrity status
 *   filter  -> ?decision=denied       -> isolate the suppression events
 *              ("the finance user was denied the ITAR document — and the trail
 *               recorded it")
 *   export  -> GET /api/audit/export?format=csv|jsonl -> download the chain
 *
 * The console is admin/leadership-only on the backend; a non-admin persona gets
 * a 403, which we surface as a clear "restricted" message rather than an error.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { AUTH_TOKEN_KEY, config } from '../lib/config';
import { SensitivityBadgeList } from '../components/SensitivityBadge';

interface AuditEvent {
  index: number;
  event_id: string;
  event_hash: string;
  ts: string;
  actor: string;
  actor_groups: string[];
  action: string;
  query: string | null;
  candidates: number;
  returned: number;
  excluded_for_acl: number;
  doc_refs: string[];
  decision: string;
  agent_id: string | null;
  tool: string | null;
}

interface AuditEventsPage {
  total: number;
  limit: number;
  offset: number;
  events: AuditEvent[];
}

interface VerifyResponse {
  valid: boolean;
  count: number;
}

const DECISION_FILTERS = [
  { value: '', label: 'All decisions' },
  { value: 'denied', label: 'Denied (suppressions)' },
  { value: 'allowed', label: 'Allowed' },
  { value: 'partial', label: 'Partial' },
] as const;

const DECISION_BADGE: Record<string, string> = {
  denied: 'bg-red-100 text-red-800',
  deny: 'bg-red-100 text-red-800',
  partial: 'bg-amber-100 text-amber-800',
  allowed: 'bg-emerald-100 text-emerald-800',
  allow: 'bg-emerald-100 text-emerald-800',
};

function exportUrl(format: 'csv' | 'jsonl'): string {
  return `${config.apiBaseUrl}/audit/export?format=${format}`;
}

/** Trigger a download of the export, attaching the bearer token via fetch. */
async function downloadExport(format: 'csv' | 'jsonl'): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
  const res = await fetch(exportUrl(format), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit_export.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function Audit(): ReactNode {
  const [decision, setDecision] = useState('');

  const eventsQuery = useQuery({
    queryKey: ['audit-events', decision],
    queryFn: () =>
      api.get<AuditEventsPage>(
        `/audit/events?limit=100${decision ? `&decision=${decision}` : ''}`,
      ),
    retry: false,
  });

  const verifyQuery = useQuery({
    queryKey: ['audit-verify'],
    queryFn: () => api.get<VerifyResponse>('/audit/verify'),
    retry: false,
  });

  const forbidden =
    eventsQuery.error instanceof ApiError && eventsQuery.error.status === 403;

  return (
    <div className="flex flex-col gap-5 animate-fade-in" data-testid="audit-console">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-navy-900">Audit Trail</h2>
          <p className="mt-1 text-sm text-steel-600">
            Append-only, hash-chained record of every access decision. Filter by{' '}
            <span className="font-medium text-navy-700">Denied</span> to review
            blocked access attempts.
          </p>
        </div>
        {verifyQuery.data && (
          <span
            data-testid="chain-status"
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
              verifyQuery.data.valid
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {verifyQuery.data.valid ? 'Chain verified' : 'Chain TAMPERED'} ·{' '}
            {verifyQuery.data.count} events
          </span>
        )}
      </header>

      {forbidden ? (
        <div
          role="alert"
          data-testid="audit-forbidden"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          The audit trail is restricted to platform administrators. Contact your
          administrator if you need access.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <label
                htmlFor="audit-decision"
                className="text-sm font-medium text-navy-900"
              >
                Decision
              </label>
              <select
                id="audit-decision"
                data-testid="audit-decision-filter"
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                className="rounded-lg border border-steel-300 px-2 py-1 text-sm focus:border-navy-500 focus:outline-none"
              >
                {DECISION_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void downloadExport('csv')}
                className="rounded-lg border border-steel-300 bg-white px-3 py-1 text-sm text-navy-700 hover:bg-steel-50"
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => void downloadExport('jsonl')}
                className="rounded-lg border border-steel-300 bg-white px-3 py-1 text-sm text-navy-700 hover:bg-steel-50"
              >
                Export JSONL
              </button>
            </div>
          </div>

          {eventsQuery.isLoading && (
            <p className="text-sm text-steel-500">Loading audit events…</p>
          )}

          {eventsQuery.isError && !forbidden && (
            <p className="text-sm text-rose-600" data-testid="audit-error">
              {eventsQuery.error instanceof ApiError
                ? eventsQuery.error.message
                : 'Failed to load audit events.'}
            </p>
          )}

          {eventsQuery.data && eventsQuery.data.events.length === 0 && (
            <p className="text-sm text-steel-500">
              No audit events match this filter yet.
            </p>
          )}

          {eventsQuery.data && eventsQuery.data.events.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-steel-200">
              <table className="w-full text-sm">
                <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">When</th>
                    <th className="px-4 py-2 font-medium">Actor</th>
                    <th className="px-4 py-2 font-medium">Groups</th>
                    <th className="px-4 py-2 font-medium">Action</th>
                    <th className="px-4 py-2 font-medium">Query</th>
                    <th className="px-4 py-2 font-medium text-right">
                      Ret / Cand
                    </th>
                    <th className="px-4 py-2 font-medium text-right">
                      ACL excl.
                    </th>
                    <th className="px-4 py-2 font-medium">Decision</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-steel-100">
                  {eventsQuery.data.events.map((ev) => (
                    <tr
                      key={ev.event_id}
                      data-testid="audit-row"
                      className="align-top hover:bg-steel-50"
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-steel-500">
                        {formatDateTime(ev.ts)}
                      </td>
                      <td className="px-4 py-2 text-steel-700">{ev.actor}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {ev.actor_groups.map((g) => (
                            <span
                              key={g}
                              className="rounded bg-steel-100 px-1.5 py-0.5 text-xs text-steel-600"
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-steel-700">
                        {ev.action}
                        {ev.tool && (
                          <span className="block text-[11px] text-steel-400">
                            {ev.tool}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 max-w-xs truncate text-navy-900">
                        {ev.query ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-steel-700">
                        {ev.returned} / {ev.candidates}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-steel-700">
                        {ev.excluded_for_acl}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          data-testid="audit-decision"
                          className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                            DECISION_BADGE[ev.decision.toLowerCase()] ??
                            'bg-steel-100 text-steel-600'
                          }`}
                        >
                          {ev.decision}
                        </span>
                        {ev.doc_refs.length > 0 && (
                          <div className="mt-1">
                            <SensitivityBadgeList sensitivity={ev.doc_refs} />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Format an ISO timestamp as a short local date-time (falls back to raw). */
function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default Audit;
